/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from '../../utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { iconButtonSize } from '../../registry/constants';
import { defaultValue } from './IconButton';
import { isAllowedInsideParentDuplicateCheck } from '../../utils/ManagerUtils';

const name = 'Icon Button';

const IconButtonManager: ComponentManagerFn<'IconButton'> = (
  platform: Platform,
): ComponentManager<'IconButton'> => {
  return {
    ...baseComponentManager<'IconButton'>(platform, 'IconButton'),
    type: 'IconButton',
    name,
    isChildAllowed: (page, item, childType) => {
      const parentType = page[item.parent]?.type;
      let allowedChildren: ItemType[] = [];
      switch (parentType) {
        case 'SidebarHeader':
        case 'SidebarDraggableList':
          allowedChildren = ['TriggerOnClick', 'Menu'];
          break;
        default:
          allowedChildren = ['TriggerOnClick', 'Menu', 'PulseTip'];
      }
      return (allowedChildren as ItemType[]).includes(childType);
    },
    isAllowedInsideParent: (page, item, context) =>
      isAllowedInsideParentDuplicateCheck(
        platform,
        page,
        item,
        'IconButton',
        context,
        ['SidebarHeader', 'SidebarDraggableList'],
      ),
    defaultValue,
    defaultChildren: ['TriggerOnClick'],
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ColorExpression',
        placeholder: 'e.g. primary or #444',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: iconButtonSize,
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
        parentColorProp: 'color',
        useParentColor: true,
      },
      ...toolTipProps,
      commentsItemId: {
        label: 'Comments Item ID',
        type: 'ItemID',
        initialFilter: 'Comments',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Comments') {
            return `Item id '${value}' doesn't belong to a Comments Component`;
          }
          return '';
        },
      },
    },
    isPropertyVisible: (property, itemProps, context) => {
      const { appUid, pageId, itemId } = context;
      const page = platform.getPage(appUid, pageId);
      const item = platform.getItem(appUid, pageId, itemId);
      if (!item) {
        platform.showError(`Missing item with ID: ${itemId}!`);
        return false;
      }
      const parent = page[item.parent];
      const parentType = parent.type;
      if (property === 'commentsItemId') {
        return (
          parentType !== 'SidebarDraggableList' &&
          parentType !== 'SidebarHeader'
        );
      }
      if (property === 'size' || property === 'icon' || property === 'color')
        return parentType !== 'SidebarDraggableList';
      return true;
    },
    category: 'Toolbar',
    allowClick: true,
    getDisplayText: (item) => '',
    replaceableWith: ['ActionButton', 'Button', 'Icon'],
    icon: (item) => {
      if (item.props.icon?.iconName) {
        return item.props.icon;
      }
      return { iconName: 'icons' };
    },
    generateShortId: (item) => item.icon?.iconName,
    description: `**Overview**<br/>
The Icon Button component is a clickable UI element that is represented primarily by an icon, rather than text. It behaves like a standard button (supporting clicks, disable states, and tooltips) but occupies less space and provides a cleaner aesthetic for common actions.
Use this component for toolbar actions (e.g., "Save," "Print"), navigation triggers (e.g., "Menu," "Back"), or item-specific actions in a list (e.g., "Delete," "Edit").

**1. Structure & Functionality**<br/>
* **Default Child:** Automatically includes a TriggerOnClick child, which is where you define the logic executed upon clicking.
* **Menu Support:** It can also contain a Menu child, turning the button into a trigger for a dropdown menu (e.g., a "More Options" kebab menu).
* **Comments Integration:** Uniquely supports linking to a specific Comments component via Comments Item ID. This is likely used to toggle the visibility of a comments panel or scroll to a discussion thread.

**2. Configuration**<br/>
* **Icon:** The visual symbol for the button.
* **Color:** Supports ColorExpression (e.g., primary, #FF0000) to style the icon. Note that when used inside certain parents like SidebarDraggableList, the color/icon properties might be hidden as they are controlled by the parent list's data.
* **Size:** Adjustable size presets (Small, Medium, Large).
* **Disable Condition:** An expression that programmatically disables the button.

**3. Context-Aware Behavior**<br/>
The component behaves differently depending on where it is placed (parentType):
* **Sidebar Context:** Inside SidebarHeader or SidebarDraggableList, it is restricted to specific children (TriggerOnClick, Menu) and may hide certain visual properties (like size or color) to enforce the sidebar's layout consistency.

**4. Best Use Cases**<br/>
* **Toolbars:** "Search," "Filter," and "Notifications" icons in an app header.
* **Data Grids:** "Edit" (Pencil) and "Delete" (Trash) actions in a row.
* **Floating Actions:** A secondary action button floating in the corner of a card.`,
  };
};

export default IconButtonManager;
